import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, PenLine, X } from 'lucide-react';
import ModelPicker from '../Common/ModelPicker';
import { sceneWritingApi } from '../../api/sceneWriting';
import { settingsApi } from '../../api/settings';
import { betaAiPaused } from '../../utils/betaSpendPause';
import { creditsErrorCopy } from '../../utils/betaAllowance';
import {
  CANT_ESTIMATE_NOW_LINE,
  CANT_ESTIMATE_RATES_LINE,
  CANT_ESTIMATE_SIZE_LINE,
  ESTIMATE_SLOW_MS,
  PROSE_SCENE_GENERATION_KEY,
  estimateSceneWritingCostUsd,
  formatSceneWritingEstimateLine,
  pricingFromCatalog,
  resolveProseModelId,
} from '../../utils/estimateSceneWritingCost';
import { formatEstimateAbout, formatEstimateRange } from '../../utils/estimateOutlineCost';
import {
  MAX_TAKES,
  OVERLAY_DRIFT_PX,
  TINY_WORD_COUNT,
  UNDO_MS,
  contextRuleRanges,
  cleanPassageReplacement,
  countWords,
  coversMostOfScene,
  followPassage,
  instructionTooLong,
  passageCallDidNotRun,
  passageErrorKind,
  passageMirrorPieces,
  passageColumnWidth,
  readPassageLayout,
  resolveSelectionSpan,
  rewriteReplacementSpan,
  shortcutLabel,
  splicePassage,
  visibleDraftBesidePanel,
} from '../../utils/passageRewrite';
import {
  ADD_SCENE_CANCEL,
  REWRITE_PASSAGE_CHANGED,
  REWRITE_PASSAGE_DONE,
  REWRITE_PASSAGE_FAILED,
  REWRITE_PASSAGE_FAILED_FREE,
  REWRITE_PASSAGE_GO,
  REWRITE_PASSAGE_INSTRUCTION,
  REWRITE_PASSAGE_INSTRUCTION_COUNT_FROM,
  REWRITE_PASSAGE_INSTRUCTION_LIMIT,
  REWRITE_PASSAGE_INSTRUCTION_TOO_LONG,
  REWRITE_PASSAGE_JUST_THIS,
  REWRITE_PASSAGE_KEEP_MINE,
  REWRITE_PASSAGE_LENGTH,
  REWRITE_PASSAGE_LONGER,
  REWRITE_PASSAGE_MOST,
  REWRITE_PASSAGE_NEED_WORDS,
  REWRITE_PASSAGE_NEW,
  REWRITE_PASSAGE_NOTES,
  REWRITE_PASSAGE_READS_OFF,
  REWRITE_PASSAGE_READS_ON,
  REWRITE_PASSAGE_SAME,
  REWRITE_PASSAGE_SAVE_FAILED,
  REWRITE_PASSAGE_SAVING,
  REWRITE_PASSAGE_SELECT_AGAIN,
  REWRITE_PASSAGE_SHORTER,
  REWRITE_PASSAGE_TINY,
  REWRITE_PASSAGE_TRY_AGAIN,
  REWRITE_PASSAGE_UNDONE,
  REWRITE_PASSAGE_USE_NEW,
  REWRITE_PASSAGE_WHOLE_SCENE,
  REWRITE_PASSAGE_YOURS,
  REWRITE_THIS,
  UNDO_ACTION,
  UNDO_DISMISS,
  rewritePassageInstructionCount,
  rewritePassageReadyLine,
  rewritePassageSelectionCount,
  rewritePassageTakeLabel,
  rewritePassageTryAgain,
} from '../../utils/writerCopy';
import './PassageRewrite.css';

const ESTIMATE_WAIT_MS = 250;

function undoShortcut(event) {
  const key = String(event.key || '').toLowerCase();
  const code = String(event.code || '');
  return (event.ctrlKey || event.metaKey)
    && !event.shiftKey
    && !event.altKey
    && (key === 'z' || code === 'KeyZ');
}

const LENGTHS = [
  ['shorter', REWRITE_PASSAGE_SHORTER],
  ['same', REWRITE_PASSAGE_SAME],
  ['longer', REWRITE_PASSAGE_LONGER],
];

function usePassageLayout(editorRef) {
  const [layout, setLayout] = useState(readPassageLayout);
  useEffect(() => {
    const apply = () => {
      const editor = editorRef.current;
      setLayout(readPassageLayout({
        visibleDraft: visibleDraftBesidePanel(editor),
        columnWidth: passageColumnWidth(editor),
      }));
    };
    apply();
    window.addEventListener('resize', apply);
    window.visualViewport?.addEventListener('resize', apply);
    let observer;
    const editor = editorRef.current;
    const column = editor?.closest?.('.sw-prose-field, .sw-writing') || editor;
    if (typeof ResizeObserver === 'function' && column) {
      observer = new ResizeObserver(apply);
      observer.observe(column);
    }
    return () => {
      window.removeEventListener('resize', apply);
      window.visualViewport?.removeEventListener('resize', apply);
      observer?.disconnect();
    };
  }, [editorRef]);
  return layout;
}

function costBand(catalog, modelId, size) {
  if (!catalog || !modelId || !size) return null;
  const pricing = pricingFromCatalog(catalog, modelId);
  if (!pricing) return null;
  // Same wiring as Write draft. output_budget is the call's stop, not the
  // shown band. A passage estimate has no draft_check and no note_facts.
  return estimateSceneWritingCostUsd(pricing, {
    estimatedInputTokens: size.estimated_input_tokens ?? size.estimatedInputTokens,
    promptChars: size.prompt_chars ?? size.promptChars,
    typicalOutput: size.typical_output_tokens ?? size.typicalOutput,
    outputBudget: size.worst_output_tokens ?? size.worstOutput,
  });
}

/**
 * Select a passage in the scene draft and rewrite only that passage (#2671).
 * The editor stays a plain textarea. On a desktop-width layout a mirror draws
 * the selection wash and the ruled paragraphs behind it. Phone, a full-screen
 * 200% page, a narrow draft column, and any mirror that drifts keep the
 * browser's own selection. A narrow column stacks the panel. A column
 * too narrow to read stacks it at the full width of the page.
 */
export default function PassageRewrite({
  id = 'sw-prose-edit',
  className = '',
  value,
  onChange,
  onBlur,
  onCommit,
  flushDraft,
  textareaRef,
  storyId,
  sceneId,
  disabled = false,
  accepted = false,
  label,
  describedBy,
  onWholeSceneRewrite,
  onCreditsPause,
  onActiveChange,
}) {
  const editorRef = useRef(null);
  const layout = usePassageLayout(editorRef);
  const innerRef = useRef(null);
  const mirrorRef = useRef(null);
  const instructionRef = useRef(null);
  const useNewRef = useRef(null);
  const passageRef = useRef(null);
  const valueRef = useRef(value);
  const takesRef = useRef([]);
  const undoRef = useRef(null);
  const undoFn = useRef(() => {});
  const typedSinceUndo = useRef(false);
  const toastKind = useRef(null);
  const toastRemain = useRef(UNDO_MS);
  const toastDeadline = useRef(0);
  const toastHeld = useRef(false);
  const toastHolds = useRef(0);
  const toastTimer = useRef(0);
  const toastNodeRef = useRef(null);
  const busyRef = useRef(false);
  const modelTouched = useRef(false);
  valueRef.current = value;

  const [live, setLive] = useState(null);
  const [passage, setPassage] = useState(null);
  const [phase, setPhase] = useState('idle');
  const [partialOk, setPartialOk] = useState(false);
  const [instruction, setInstruction] = useState('');
  const [instructionError, setInstructionError] = useState('');
  const [length, setLength] = useState('same');
  const [useNotes, setUseNotes] = useState(true);
  const [modelId, setModelId] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [size, setSize] = useState(null);
  const [sizeState, setSizeState] = useState('idle');
  const [slowEstimate, setSlowEstimate] = useState(false);
  const [takes, setTakes] = useState([]);
  const [takeIndex, setTakeIndex] = useState(0);
  const [askAnyway, setAskAnyway] = useState(false);
  const [saveNote, setSaveNote] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [creditsMessage, setCreditsMessage] = useState('');
  const [toast, setToast] = useState(null);
  const [undoReady, setUndoReady] = useState(false);
  const [overlayOn, setOverlayOn] = useState(true);
  const [chipTop, setChipTop] = useState(8);

  passageRef.current = passage;
  takesRef.current = takes;

  const setArea = (node) => {
    innerRef.current = node;
    if (typeof textareaRef === 'function') textareaRef(node);
    else if (textareaRef) textareaRef.current = node;
  };

  const showCompare = (phase === 'compare' || phase === 'error') && (takes.length > 0 || phase === 'error');
  const showPanel = phase === 'panel' || phase === 'running';
  const locked = phase === 'running';
  // While this flow is open, Rewrite or Use new is the one filled button.
  const passageGo = 'sw-passage-go';
  const desktop = layout === 'desktop';
  const activeRange = passage || (phase === 'idle' ? live : null);
  const most = passage ? coversMostOfScene(value, value.slice(passage.start, passage.end)) : false;
  const words = passage ? countWords(value.slice(passage.start, passage.end)) : (live ? countWords(live.text) : 0);
  const band = costBand(catalog, modelId, size);
  const aboutCost = band ? formatEstimateAbout(formatEstimateRange(band.typical, band.worst)) : null;
  const saveFailed = saveNote === REWRITE_PASSAGE_SAVE_FAILED;
  const saving = saveNote === REWRITE_PASSAGE_SAVING;
  const instructionOver = instruction.length > REWRITE_PASSAGE_INSTRUCTION_LIMIT;
  const showInstructionCount = instruction.length >= REWRITE_PASSAGE_INSTRUCTION_COUNT_FROM;
  const blankSelection = Boolean(passage && !String(passage.text || '').trim());
  const rewriteOff = Boolean(creditsMessage)
    || saveFailed
    || saving
    || instructionOver
    || blankSelection
    || phase === 'running'
    || disabled;

  let estimateLine = 'Loading estimate…';
  if (catalog && catalog.length === 0) estimateLine = CANT_ESTIMATE_RATES_LINE;
  else if (!catalog && slowEstimate) estimateLine = CANT_ESTIMATE_NOW_LINE;
  else if (catalog && (sizeState === 'missing' || sizeState === 'changed')) {
    estimateLine = CANT_ESTIMATE_SIZE_LINE;
  }
  else if (catalog && sizeState === 'ready') {
    estimateLine = formatSceneWritingEstimateLine(band) || CANT_ESTIMATE_RATES_LINE;
  } else if (slowEstimate && sizeState !== 'ready') estimateLine = CANT_ESTIMATE_NOW_LINE;

  const closeToDraft = () => {
    busyRef.current = false;
    setPhase('idle');
    setPassage(null);
    setTakes([]);
    takesRef.current = [];
    setTakeIndex(0);
    setAskAnyway(false);
    setErrorMessage('');
    setCreditsMessage('');
    setSaveNote('');
    setInstructionError('');
    setPartialOk(false);
    window.setTimeout(() => innerRef.current?.focus(), 0);
  };

  const selectionFrom = (el) => {
    if (!el) return null;
    const start = Math.min(el.selectionStart ?? 0, el.selectionEnd ?? 0);
    const end = Math.max(el.selectionStart ?? 0, el.selectionEnd ?? 0);
    if (start === end) return null;
    const text = el.value.slice(start, end);
    if (!text) return null;
    const snapped = resolveSelectionSpan(el.value, start, end);
    // A space-only selection stays as the writer marked it. Snapping that
    // would be empty, and Rewrite stays off until there are words.
    if (!snapped.text.trim()) return { start, end, text };
    return { start: snapped.start, end: snapped.end, text: snapped.text };
  };

  const sameRange = (a, b) => Boolean(
    a && b && a.start === b.start && a.end === b.end && a.text === b.text,
  );

  const openPanel = () => {
    if (disabled || phase !== 'idle') return;
    const fresh = selectionFrom(innerRef.current);
    const next = fresh?.text ? fresh : (live?.text ? live : null);
    if (!next?.text) return;
    modelTouched.current = false;
    setLive(next);
    setPassage({ ...next });
    setPhase('panel');
    setPartialOk(false);
    setInstruction('');
    setLength('same');
    setUseNotes(true);
    setTakes([]);
    setTakeIndex(0);
    setSize(null);
    setSizeState('loading');
    setErrorMessage('');
    setCreditsMessage('');
    setSaveNote('');
    setInstructionError('');
    setAskAnyway(false);
  };

  const readSelection = (el) => {
    if (!el || disabled || (phase !== 'idle' && phase !== 'panel')) return;
    const next = selectionFrom(el);
    if (phase === 'panel') {
      if (!next || sameRange(passageRef.current, next)) return;
      setLive(next);
      setPassage({ ...next });
      return;
    }
    setLive(next);
  };

  const trackTakes = (prev, next) => {
    const nextTakes = takesRef.current.map((take) => {
      if (!take.span) return take;
      const moved = followPassage(prev, next, take.span, { markChanged: true });
      return {
        ...take,
        span: moved.passage || take.span,
        spanChanged: Boolean(take.spanChanged || moved.changed),
      };
    });
    takesRef.current = nextTakes;
    setTakes(nextTakes);
  };

  const onDraftChange = (event) => {
    const next = event.target.value;
    const current = valueRef.current;
    // Assigning the controlled value can emit input without a keystroke.
    // That must not disarm Ctrl+Z. A real edit changes the text.
    if (next === current) return;
    const stored = passageRef.current;
    const tracking = stored && (
      phase === 'running' || phase === 'panel' || phase === 'error' || phase === 'compare'
    );
    if (tracking) {
      const followed = followPassage(current, next, stored, {
        lockEdits: phase === 'running',
        markChanged: phase !== 'running',
      });
      if (followed.rejected) {
        event.target.value = current;
        return;
      }
      passageRef.current = followed.passage;
      setPassage(followed.passage);
      trackTakes(current, next);
      valueRef.current = followed.text;
      onChange(followed.text);
      return;
    }
    if (undoRef.current || toastKind.current === 'applied') {
      typedSinceUndo.current = true;
    }
    if (undoRef.current) {
      const undoSpan = {
        start: undoRef.current.at,
        end: undoRef.current.at + undoRef.current.inserted.length,
        text: undoRef.current.inserted,
      };
      const moved = followPassage(current, next, undoSpan, { markChanged: true });
      if (moved.changed) {
        undoRef.current = null;
        setUndoReady(false);
        if (toastKind.current === 'applied') {
          window.clearTimeout(toastTimer.current);
          toastTimer.current = 0;
          toastKind.current = null;
          setToast(null);
        }
      } else if (moved.passage) {
        undoRef.current.at = moved.passage.start;
      }
    }
    valueRef.current = next;
    onChange(next);
  };

  const runRewrite = async () => {
    if (busyRef.current || rewriteOff) return;
    const stored = passageRef.current;
    if (!stored || !storyId || !sceneId) return;
    if (takesRef.current.length >= MAX_TAKES) return;
    if (most && !partialOk) return;
    if (betaAiPaused()) {
      onCreditsPause?.();
      return;
    }
    busyRef.current = true;
    setErrorMessage('');
    setCreditsMessage('');
    setSaveNote(REWRITE_PASSAGE_SAVING);
    let kept = true;
    try {
      kept = flushDraft ? await flushDraft() : true;
    } catch {
      kept = false;
    }
    if (kept !== true) {
      busyRef.current = false;
      setPhase(takesRef.current.length ? 'compare' : 'panel');
      setSaveNote(kept === false ? REWRITE_PASSAGE_SAVE_FAILED : '');
      return;
    }
    const text = valueRef.current;
    const rawStart = Math.max(0, Math.min(stored.start, text.length));
    const rawEnd = Math.max(rawStart, Math.min(stored.end, text.length));
    const snapped = resolveSelectionSpan(text, rawStart, rawEnd);
    const start = snapped.text.trim() ? snapped.start : rawStart;
    const end = snapped.text.trim() ? snapped.end : rawEnd;
    const selectedText = text.slice(start, end);
    if (!selectedText.trim()) {
      busyRef.current = false;
      setSaveNote('');
      setPhase('panel');
      return;
    }
    // This is the span sent for rewrite. Use new puts the reply over it,
    // including a word the browser selection had cut in half.
    const frozen = { start, end, text: selectedText };
    passageRef.current = frozen;
    setPassage(frozen);
    setPhase('running');
    setSaveNote('');
    const body = {
      selectionStart: start,
      selectionEnd: end,
      selectedText,
      instruction: instruction.trim() || null,
      length,
      modelId: modelId || null,
      useNotesAndStyle: useNotes,
    };
    try {
      const result = await sceneWritingApi.rewritePassage(storyId, sceneId, body);
      const written = String(result?.text || '');
      if (!written.trim()) {
        const empty = new Error('empty');
        empty.status = 502;
        throw empty;
      }
      const reported = Number(result.word_count ?? result.wordCount);
      const current = passageRef.current || frozen;
      const delta = current.text === frozen.text ? current.start - frozen.start : 0;
      const reportedStart = Number(result.selection_start ?? result.selectionStart);
      const reportedEnd = Number(result.selection_end ?? result.selectionEnd);
      const selected = result.selected_text ?? result.selectedText;
      // Edits above the passage move the stored span by delta. The slice is
      // checked again in applyTake, and a miss does not search the draft.
      // The reply replaces the snapped range that was sent. A shorter range
      // from the response used to leave the selection's last word behind.
      let span = {
        start: frozen.start + delta,
        end: frozen.end + delta,
        text: frozen.text,
      };
      if (Number.isFinite(reportedStart) && Number.isFinite(reportedEnd) && typeof selected === 'string') {
        const at = reportedStart + delta;
        const to = reportedEnd + delta;
        const currentText = valueRef.current;
        const reportedMatches = at >= 0
          && to >= at
          && to <= currentText.length
          && currentText.slice(at, to) === selected;
        if (!reportedMatches) span = { start: at, end: to, text: selected };
      }
      const take = {
        text: written,
        wordCount: Number.isFinite(reported) ? reported : countWords(written),
        span,
        spanChanged: false,
      };
      const nextTakes = [...takesRef.current, take].slice(0, MAX_TAKES);
      takesRef.current = nextTakes;
      setTakes(nextTakes);
      setTakeIndex(nextTakes.length - 1);
      setPhase('compare');
    } catch (err) {
      const kind = passageErrorKind(err);
      if (kind === 'instruction') {
        setInstructionError(REWRITE_PASSAGE_INSTRUCTION_TOO_LONG);
        setErrorMessage('');
        setPhase('panel');
      } else if (kind === 'credits') {
        setCreditsMessage(err?.message || creditsErrorCopy(err).message);
        setPhase(takesRef.current.length ? 'compare' : 'panel');
      } else if (kind === 'passage_changed') {
        setErrorMessage(REWRITE_PASSAGE_SELECT_AGAIN);
        setPhase(takesRef.current.length ? 'compare' : 'error');
      } else {
        setErrorMessage(passageCallDidNotRun(err) ? REWRITE_PASSAGE_FAILED_FREE : REWRITE_PASSAGE_FAILED);
        setPhase(takesRef.current.length ? 'compare' : 'error');
      }
    } finally {
      busyRef.current = false;
    }
  };

  const stopToastTimer = () => {
    window.clearTimeout(toastTimer.current);
    toastTimer.current = 0;
  };

  const startToastTimer = (ms) => {
    stopToastTimer();
    const wait = Math.max(0, ms);
    toastRemain.current = wait;
    if (toastHeld.current) return;
    toastDeadline.current = Date.now() + wait;
    const closeToastLater = () => {
      const node = toastNodeRef.current;
      const hot = Boolean(node && (node.matches(':hover') || node.matches(':focus-within')));
      if (hot) {
        toastHeld.current = true;
        toastRemain.current = UNDO_MS;
        toastTimer.current = window.setTimeout(closeToastLater, 250);
        return;
      }
      undoRef.current = null;
      setUndoReady(false);
      toastKind.current = null;
      setToast(null);
    };
    toastTimer.current = window.setTimeout(closeToastLater, wait);
  };

  const holdToast = (held) => {
    toastHolds.current += held ? 1 : -1;
    if (toastHolds.current < 0) toastHolds.current = 0;
    const nowHeld = toastHolds.current > 0;
    if (nowHeld && !toastHeld.current) {
      toastHeld.current = true;
      if (toastTimer.current) {
        toastRemain.current = Math.max(0, toastDeadline.current - Date.now());
        stopToastTimer();
      }
      return;
    }
    if (!nowHeld && toastHeld.current) {
      toastHeld.current = false;
      if (toastKind.current) startToastTimer(toastRemain.current);
    }
  };

  const showToast = (kind) => {
    toastHolds.current = 0;
    toastHeld.current = false;
    toastKind.current = kind;
    setToast(kind);
    startToastTimer(UNDO_MS);
  };

  const closeToast = () => {
    toastHolds.current = 0;
    toastHeld.current = false;
    stopToastTimer();
    toastKind.current = null;
    undoRef.current = null;
    setUndoReady(false);
    setToast(null);
  };

  const commit = (next, swap) => {
    if (swap) {
      undoRef.current = {
        at: swap.start,
        inserted: swap.inserted,
        removed: swap.removed,
      };
      setUndoReady(true);
    }
    typedSinceUndo.current = false;
    onChange(next);
    onCommit?.(next);
    setPhase('idle');
    setPassage(null);
    passageRef.current = null;
    setTakes([]);
    takesRef.current = [];
    setTakeIndex(0);
    setAskAnyway(false);
    setErrorMessage('');
    setCreditsMessage('');
    setInstructionError('');
    setSaveNote('');
    showToast('applied');
  };

  const undo = () => {
    const snap = undoRef.current;
    if (!snap) return;
    const draft = valueRef.current;
    const end = snap.at + snap.inserted.length;
    if (draft.slice(snap.at, end) !== snap.inserted) {
      undoRef.current = null;
      setUndoReady(false);
      return;
    }
    const restored = splicePassage(draft, snap.at, end, snap.removed);
    undoRef.current = null;
    setUndoReady(false);
    typedSinceUndo.current = true;
    valueRef.current = restored;
    onChange(restored);
    onCommit?.(restored);
    showToast('undone');
  };
  undoFn.current = undo;

  const spanFor = (text, stored, take) => {
    const response = take?.span
      ? {
        selection_start: take.span.start,
        selection_end: take.span.end,
        selected_text: take.span.text,
      }
      : null;
    return rewriteReplacementSpan(text, stored, response);
  };

  const applyTake = () => {
    const stored = passageRef.current;
    const take = takesRef.current[takeIndex];
    if (!stored || !take) return null;
    const text = valueRef.current;
    const span = spanFor(text, stored, take);
    // Safety check: the draft at the stored span must still be selected_text.
    if (!span || span.status === 'changed' || text.slice(span.start, span.end) !== span.text) {
      return null;
    }
    const inserted = cleanPassageReplacement(text, span.start, span.end, take.text);
    return {
      next: splicePassage(text, span.start, span.end, inserted),
      swap: { start: span.start, inserted, removed: span.text },
    };
  };

  const refuseStalePassage = () => {
    setAskAnyway(false);
    setErrorMessage(REWRITE_PASSAGE_SELECT_AGAIN);
  };

  const useNew = () => {
    const applied = applyTake();
    if (!applied) {
      refuseStalePassage();
      return;
    }
    commit(applied.next, applied.swap);
  };

  const useNewAnyway = () => {
    const applied = applyTake();
    if (!applied) {
      refuseStalePassage();
      return;
    }
    setAskAnyway(false);
    commit(applied.next, applied.swap);
  };

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const flowOpen = showPanel || phase === 'compare' || phase === 'error';
  useEffect(() => {
    onActiveChange?.(flowOpen);
    return () => onActiveChange?.(false);
  }, [flowOpen, onActiveChange]);

  const onDraftKeyDown = (event) => {
    const area = innerRef.current;
    const editor = editorRef.current;
    const inDraft = event.target === area
      || (area && document.activeElement === area && editor?.contains(event.target));
    if (!inDraft) return;
    if (!undoShortcut(event) || typedSinceUndo.current || !undoRef.current || toastKind.current !== 'applied') {
      return;
    }
    event.preventDefault();
    undoFn.current();
  };

  useEffect(() => {
    const onKey = (event) => {
      const area = innerRef.current;
      if (!area || document.activeElement !== area) return;
      if (!undoShortcut(event) || typedSinceUndo.current || !undoRef.current || toastKind.current !== 'applied') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      undoFn.current();
    };
    const onBefore = (event) => {
      const area = innerRef.current;
      if (!area || event.target !== area) return;
      if (event.inputType !== 'historyUndo') return;
      if (typedSinceUndo.current || !undoRef.current || toastKind.current !== 'applied') return;
      event.preventDefault();
      event.stopPropagation();
      undoFn.current();
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('beforeinput', onBefore, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('beforeinput', onBefore, true);
    };
  }, []);

  useLayoutEffect(() => {
    if (phase !== 'idle' || !undoReady || toast !== 'applied') return undefined;
    const focusDraft = () => {
      if (toastKind.current !== 'applied') return;
      innerRef.current?.focus({ preventScroll: true });
    };
    focusDraft();
    const timer = window.setTimeout(focusDraft, 0);
    return () => window.clearTimeout(timer);
  }, [phase, undoReady, toast]);

  useEffect(() => {
    if (phase !== 'panel') return undefined;
    instructionRef.current?.focus();
    return undefined;
  }, [phase]);

  useEffect(() => {
    if (phase !== 'compare') return undefined;
    useNewRef.current?.focus();
    return undefined;
  }, [phase, takes.length]);

  useEffect(() => {
    if (showPanel || phase === 'compare' || phase === 'error') {
      const timer = window.setTimeout(() => setSlowEstimate(true), ESTIMATE_SLOW_MS);
      return () => window.clearTimeout(timer);
    }
    setSlowEstimate(false);
    return undefined;
  }, [showPanel, phase]);

  useEffect(() => {
    if (!(showPanel || phase === 'compare' || phase === 'error')) return undefined;
    let cancel = false;
    (async () => {
      try {
        const [settingsRes, catalogRes] = await Promise.all([
          settingsApi.getSettings(),
          settingsApi.getOpenRouterCatalog().catch(() => null),
        ]);
        if (cancel) return;
        const settings = settingsRes?.data || settingsRes || {};
        const preferred = resolveProseModelId(settings.model_preferences);
        if (!modelTouched.current) setModelId(preferred);
        const models = catalogRes?.data?.models || catalogRes?.models || [];
        setCatalog(Array.isArray(models) ? models : []);
      } catch {
        if (!cancel) setCatalog([]);
      }
    })();
    return () => { cancel = true; };
  }, [showPanel, phase]);

  useEffect(() => {
    if (!(showPanel || phase === 'compare' || phase === 'error') || !passage || !storyId || !sceneId) {
      return undefined;
    }
    let cancel = false;
    const timer = window.setTimeout(() => {
      const text = valueRef.current;
      const stored = passageRef.current;
      if (!stored) return;
      setSizeState('loading');
      sceneWritingApi.estimatePassageRewrite(storyId, sceneId, {
        selectionStart: stored.start,
        selectionEnd: stored.end,
        selectedText: text.slice(stored.start, stored.end),
        instruction: instruction.trim() || null,
        length,
        modelId: modelId || null,
        useNotesAndStyle: useNotes,
      }).then((payload) => {
        if (cancel) return;
        if (!payload) {
          setSize(null);
          setSizeState('missing');
          return;
        }
        setSize(payload);
        setSizeState('ready');
      }).catch((err) => {
        if (cancel) return;
        setSize(null);
        if (instructionTooLong(err)) {
          setInstructionError(REWRITE_PASSAGE_INSTRUCTION_TOO_LONG);
          setSizeState('missing');
          return;
        }
        // 409 means the saved passage moved. Do not price that selection.
        setSizeState(passageErrorKind(err) === 'passage_changed' ? 'changed' : 'missing');
      });
    }, ESTIMATE_WAIT_MS);
    return () => {
      cancel = true;
      window.clearTimeout(timer);
    };
  }, [
    showPanel,
    phase,
    passage,
    storyId,
    sceneId,
    instruction,
    length,
    modelId,
    useNotes,
  ]);

  useEffect(() => {
    const onKey = (event) => {
      const pickerOpen = document.querySelector('[data-model-picker-panel]');
      if (event.key === 'Escape' && !pickerOpen && (showPanel || phase === 'compare' || phase === 'error')) {
        event.preventDefault();
        if (phase === 'compare' || phase === 'error' || askAnyway) closeToDraft();
        else closeToDraft();
        return;
      }
      const shortcut = (event.ctrlKey || event.metaKey) && event.shiftKey && !event.altKey
        && event.key.toLowerCase() === 'r';
      if (!shortcut || phase !== 'idle' || disabled) return;
      if (document.activeElement !== innerRef.current) return;
      if (!live?.text) return;
      event.preventDefault();
      openPanel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  useLayoutEffect(() => {
    const area = innerRef.current;
    const mirror = mirrorRef.current;
    if (!desktop || !area || !mirror || showCompare) {
      setOverlayOn(false);
      return;
    }
    const bar = Math.max(0, area.offsetWidth - area.clientWidth);
    mirror.style.paddingRight = `${12 + bar}px`;
    mirror.scrollTop = area.scrollTop;
    const drift = Math.abs(mirror.scrollHeight - area.scrollHeight);
    setOverlayOn(drift <= OVERLAY_DRIFT_PX);
    const mark = mirror.querySelector('[data-passage-selection]');
    const editor = editorRef.current;
    if (mark && editor) {
      const top = mark.getBoundingClientRect().top - editor.getBoundingClientRect().top;
      setChipTop(Math.max(8, top));
    }
  }, [desktop, showCompare, value, live, passage, phase, layout]);

  const mirrorPieces = passageMirrorPieces(
    value,
    activeRange,
    passage && (showPanel || locked) ? contextRuleRanges(value, passage.start, passage.end) : [],
    locked ? passage : null,
  );

  const wide = layout === 'desktop' || layout === 'stacked';
  const chip = phase === 'idle' && live && !disabled && wide ? (
    <button
      type="button"
      className="sw-passage-chip"
      data-testid="sw-passage-chip"
      style={{ top: chipTop }}
      onClick={openPanel}
    >
      <PenLine aria-hidden="true" size={16} />
      <span>{REWRITE_THIS}</span>
      <kbd>{shortcutLabel()}</kbd>
    </button>
  ) : null;

  const phoneBar = phase === 'idle' && live && !disabled && !wide
    ? createPortal(
      <div className="sw-passage-bar" data-testid="sw-passage-bar" role="region" aria-label={`${rewritePassageSelectionCount(words)} · ${REWRITE_THIS}`}>
        <span>{rewritePassageSelectionCount(words)}</span>
        <button type="button" className="sw-passage-go" onClick={openPanel}>
          {REWRITE_THIS}
        </button>
      </div>,
      document.body,
    )
    : null;

  const panelBody = showPanel ? (
    <div
      className={`sw-passage-panel sw-passage-panel--${layout}`}
      role="dialog"
      aria-modal="false"
      aria-labelledby="sw-passage-title"
      data-testid="sw-passage-panel"
    >
      <div className="sw-passage-panel__head">
        <h2 id="sw-passage-title">{REWRITE_THIS}</h2>
        <button type="button" className="sw-passage-icon" aria-label={ADD_SCENE_CANCEL} onClick={closeToDraft}>
          <X aria-hidden="true" size={18} />
        </button>
      </div>
      <p className="sw-passage-count">{rewritePassageSelectionCount(words)}</p>
      {blankSelection ? (
        <p className="sw-passage-nudge" data-testid="sw-passage-need-words">{REWRITE_PASSAGE_NEED_WORDS}</p>
      ) : null}
      {words > 0 && words < TINY_WORD_COUNT && !most ? (
        <p className="sw-passage-nudge">{REWRITE_PASSAGE_TINY}</p>
      ) : null}
      {most && !partialOk ? (
        <div className="sw-passage-most" data-testid="sw-passage-most">
          <p>{REWRITE_PASSAGE_MOST}</p>
          <div className="sw-passage-actions">
            <button type="button" className={passageGo} onClick={() => { onWholeSceneRewrite?.(); closeToDraft(); }}>
              {REWRITE_PASSAGE_WHOLE_SCENE}
            </button>
            <button type="button" className="sw-passage-quiet" onClick={() => setPartialOk(true)}>
              {REWRITE_PASSAGE_JUST_THIS}
            </button>
          </div>
        </div>
      ) : null}
      <label className="sw-passage-label" htmlFor="sw-passage-instruction">{REWRITE_PASSAGE_INSTRUCTION}</label>
      <textarea
        id="sw-passage-instruction"
        ref={instructionRef}
        className="sw-passage-instruction"
        value={instruction}
        aria-invalid={instructionOver || instructionError ? 'true' : undefined}
        aria-describedby={[
          showInstructionCount ? 'sw-passage-instruction-count' : '',
          instructionOver || instructionError ? 'sw-passage-instruction-error' : '',
        ].filter(Boolean).join(' ') || undefined}
        onChange={(event) => {
          setInstruction(event.target.value);
          setInstructionError('');
        }}
      />
      {showInstructionCount ? (
        <p id="sw-passage-instruction-count" className="sw-passage-instruction-count" data-testid="sw-passage-instruction-count">
          {rewritePassageInstructionCount(instruction.length)}
        </p>
      ) : null}
      {instructionOver || instructionError ? (
        <p id="sw-passage-instruction-error" role="alert" data-testid="sw-passage-instruction-error">
          {REWRITE_PASSAGE_INSTRUCTION_TOO_LONG}
        </p>
      ) : null}
      <div className="sw-passage-length" role="radiogroup" aria-label={REWRITE_PASSAGE_LENGTH}>
        <span className="sw-passage-label">{REWRITE_PASSAGE_LENGTH}</span>
        {LENGTHS.map(([key, text]) => (
          <label
            key={key}
            className={length === key ? 'is-on' : ''}
            aria-checked={length === key}
          >
            <input
              type="radio"
              name="sw-passage-length"
              value={key}
              checked={length === key}
              onChange={() => setLength(key)}
            />
            {length === key ? <Check aria-hidden="true" size={16} /> : null}
            {text}
          </label>
        ))}
      </div>
      <div className="sw-passage-model">
        {/* Wording line 9: the Write draft label, not a new string. */}
        <span className="sw-passage-label">Model</span>
        <ModelPicker
          mode="select"
          functionKey={PROSE_SCENE_GENERATION_KEY}
          persistSelection={false}
          onChange={(id) => {
            modelTouched.current = true;
            setModelId(id || null);
          }}
        />
      </div>
      <label className="sw-passage-tick">
        <input
          type="checkbox"
          checked={useNotes}
          onChange={(event) => setUseNotes(event.target.checked)}
        />
        {REWRITE_PASSAGE_NOTES}
      </label>
      <p className="sw-passage-reads">{useNotes ? REWRITE_PASSAGE_READS_ON : REWRITE_PASSAGE_READS_OFF}</p>
      <p className="sw-passage-estimate" data-testid="sw-passage-estimate">{estimateLine}</p>
      {saveNote ? <p role="status">{saveNote}</p> : null}
      {creditsMessage ? <p role="alert" data-testid="sw-passage-credits">{creditsMessage}</p> : null}
      {!(most && !partialOk) ? (
        <div className="sw-passage-actions">
          <button
            type="button"
            className={passageGo}
            disabled={rewriteOff}
            aria-busy={phase === 'running' || saving ? 'true' : undefined}
            onClick={() => { void runRewrite(); }}
          >
            {REWRITE_PASSAGE_GO}
          </button>
          <button type="button" className="sw-passage-quiet" onClick={closeToDraft}>
            {ADD_SCENE_CANCEL}
          </button>
        </div>
      ) : (
        <div className="sw-passage-actions">
          <button type="button" className="sw-passage-quiet" onClick={closeToDraft}>
            {ADD_SCENE_CANCEL}
          </button>
        </div>
      )}
    </div>
  ) : null;
  const panel = panelBody && layout !== 'stacked' ? createPortal(panelBody, document.body) : null;
  const stackedPanel = layout === 'stacked' ? panelBody : null;

  const take = takes[takeIndex];
  const resolved = passage ? spanFor(value, passage, take || null) : null;
  const shownNew = take && resolved && resolved.status !== 'changed'
    ? cleanPassageReplacement(value, resolved.start, resolved.end, take.text)
    : (take?.text || '');
  const spanMismatch = Boolean(take && resolved && resolved.status === 'changed');
  const selectAgain = errorMessage === REWRITE_PASSAGE_SELECT_AGAIN || spanMismatch;
  const yoursText = resolved && resolved.status !== 'changed'
    ? resolved.text
    : (passage?.text || '');
  const inline = layout !== 'desktop';
  const compare = (phase === 'compare' || (phase === 'error' && takes.length > 0)) && passage ? (
    <div
      className={`sw-passage-compare sw-passage-compare--${layout}`}
      data-testid="sw-passage-compare"
    >
      <p role="status">{take ? rewritePassageReadyLine(take.wordCount) : ''}</p>
      {takes.length > 1 ? (
        <div role="tablist" aria-label={REWRITE_PASSAGE_NEW} className="sw-passage-takes">
          {takes.map((item, index) => (
            <button
              key={item.text + index}
              type="button"
              role="tab"
              aria-selected={index === takeIndex}
              className={index === takeIndex ? 'sw-passage-take sw-passage-take--on' : 'sw-passage-take'}
              onClick={() => setTakeIndex(index)}
            >
              {rewritePassageTakeLabel(index + 1)}
            </button>
          ))}
        </div>
      ) : null}
      {take && inline ? (
        <div className="sw-passage-inline">
          <p className="sw-passage-old">{yoursText}</p>
          <p className="sw-passage-new">{shownNew}</p>
        </div>
      ) : null}
      {take && !inline ? (
        <div className="sw-passage-columns">
          <section>
            <h3>{REWRITE_PASSAGE_YOURS}</h3>
            <p>{yoursText}</p>
          </section>
          <section>
            <h3>{REWRITE_PASSAGE_NEW}</h3>
            <p className="sw-passage-new">{shownNew}</p>
          </section>
        </div>
      ) : null}
      {selectAgain ? (
        <p role="alert" data-testid="sw-passage-error">{REWRITE_PASSAGE_SELECT_AGAIN}</p>
      ) : errorMessage ? (
        <p role="alert" data-testid="sw-passage-error">{errorMessage}</p>
      ) : null}
      {creditsMessage ? <p role="alert" data-testid="sw-passage-credits">{creditsMessage}</p> : null}
      {saveNote ? <p role="status">{saveNote}</p> : null}
      {askAnyway ? (
        <div data-testid="sw-passage-changed">
          <p>{REWRITE_PASSAGE_CHANGED}</p>
          <div className="sw-passage-actions">
            <button type="button" className={passageGo} onClick={useNewAnyway}>{REWRITE_PASSAGE_USE_NEW}</button>
            <button type="button" className="sw-passage-quiet" onClick={closeToDraft}>{REWRITE_PASSAGE_KEEP_MINE}</button>
          </div>
        </div>
      ) : (
        <div className="sw-passage-actions">
          {take ? (
            <button
              type="button"
              className={passageGo}
              ref={useNewRef}
              disabled={selectAgain}
              onClick={useNew}
            >
              {REWRITE_PASSAGE_USE_NEW}
            </button>
          ) : null}
          <button type="button" className="sw-passage-quiet" onClick={closeToDraft}>
            {REWRITE_PASSAGE_KEEP_MINE}
          </button>
          <button
            type="button"
            className="sw-passage-quiet"
            disabled={
              takes.length >= MAX_TAKES
              || phase === 'running'
              || saveFailed
              || selectAgain
            }
            onClick={() => { void runRewrite(); }}
          >
            {rewritePassageTryAgain(aboutCost)}
          </button>
        </div>
      )}
    </div>
  ) : null;

  const errorCard = phase === 'error' && takes.length === 0 ? (
    <div className={`sw-passage-compare sw-passage-compare--${layout}`} data-testid="sw-passage-compare" data-reason={errorMessage === REWRITE_PASSAGE_FAILED_FREE ? 'free' : 'ran'}>
      <p role="alert" data-testid="sw-passage-error">{errorMessage}</p>
      {saveNote ? <p role="status">{saveNote}</p> : null}
      <div className="sw-passage-actions">
        <button
          type="button"
          className={passageGo}
          disabled={errorMessage === REWRITE_PASSAGE_SELECT_AGAIN}
          onClick={() => { void runRewrite(); }}
        >
          {REWRITE_PASSAGE_TRY_AGAIN}
        </button>
        <button type="button" className="sw-passage-quiet" onClick={closeToDraft}>
          {REWRITE_PASSAGE_KEEP_MINE}
        </button>
      </div>
    </div>
  ) : null;

  const placeBleed = (node) => (
    layout === 'bleed' && node ? createPortal(node, document.body) : node
  );

  const toastNode = toast ? createPortal(
    <div
      className="sw-passage-toast"
      role="status"
      tabIndex={-1}
      data-testid="sw-passage-toast"
      ref={toastNodeRef}
      onMouseEnter={() => holdToast(true)}
      onMouseLeave={() => holdToast(false)}
      onFocus={() => holdToast(true)}
      onBlur={() => holdToast(false)}
    >
      <span>{toast === 'undone' ? REWRITE_PASSAGE_UNDONE : REWRITE_PASSAGE_DONE}</span>
      {toast === 'applied' && undoReady ? (
        <button type="button" onClick={undo}>{UNDO_ACTION}</button>
      ) : null}
      <button type="button" onClick={closeToast}>{UNDO_DISMISS}</button>
    </div>,
    document.body,
  ) : null;

  const aligned = Boolean(resolved && resolved.status !== 'changed');
  const parked = phase === 'compare' || phase === 'error';
  // A stale span must not hide the draft. Show every current word until
  // the writer selects the passage again.
  const before = aligned ? value.slice(0, resolved.start) : (parked ? value : '');
  const after = aligned ? value.slice(resolved.end) : '';

  return (
    <div
      className={`sw-passage-editor${locked ? ' sw-passage-editor--locked' : ''}${locked && !overlayOn ? ' sw-passage-editor--grey' : ''}`}
      ref={editorRef}
      data-overlay={desktop && overlayOn && !parked ? 'on' : 'off'}
      data-layout={layout}
      data-accepted={accepted ? 'true' : 'false'}
    >
      {stackedPanel}
      {parked && before ? <div className="sw-passage-context">{before}</div> : null}
      {placeBleed(compare)}
      {placeBleed(errorCard)}
      {parked && after ? <div className="sw-passage-context">{after}</div> : null}
      {desktop && !parked && activeRange ? (
        <div className="sw-passage-mirror" ref={mirrorRef} aria-hidden="true">
          {mirrorPieces.map((piece) => {
            const className = [
              piece.kinds.includes('rule') ? 'sw-passage-rule' : '',
              piece.kinds.includes('selection') ? 'sw-passage-mark' : '',
              piece.kinds.includes('lock') ? 'sw-passage-lock' : '',
            ].filter(Boolean).join(' ');
            if (!className) return <span key={`${piece.start}-${piece.end}`}>{piece.text}</span>;
            return (
              <span
                key={`${piece.start}-${piece.end}`}
                className={className}
                data-passage-selection={piece.kinds.includes('selection') ? '' : undefined}
              >
                {piece.text}
              </span>
            );
          })}
        </div>
      ) : null}
      <textarea
        id={id}
        ref={setArea}
        className={`${className}${parked ? ' sw-passage-textarea--parked' : ''}`}
        value={value}
        disabled={disabled}
        aria-label={label}
        aria-describedby={describedBy}
        onChange={onDraftChange}
        onBlur={onBlur}
        onKeyDown={onDraftKeyDown}
        onScroll={(event) => {
          if (mirrorRef.current) mirrorRef.current.scrollTop = event.target.scrollTop;
        }}
        onMouseUp={(event) => readSelection(event.target)}
        onKeyUp={(event) => readSelection(event.target)}
        onSelect={(event) => readSelection(event.target)}
      />
      {chip}
      {phoneBar}
      {panel}
      {toastNode}
    </div>
  );
}
